import React, { useEffect } from 'react'
import {
  AdvancedMarker,
  InfoWindow,
  Map,
  useAdvancedMarkerRef,
  useMap
} from '@vis.gl/react-google-maps'
import { createGlobalStyle } from 'styled-components'
import { color } from 'styles/colors'

import { Customer } from 'types/DeviceActivity'

import Marker from './Marker'

import { useSessionContext } from 'hooks/SessionContext'

type Props = {
  customersWithLocation: Array<Customer>
  selectedCustomerId: string | null
  onCustomerSelectCallback: (customerId: string | null) => void
}

const markerIcon = (customer: Customer): string => {
  const criticalCount = Math.max(...customer.devices.map((d) => d.criticalAlarmCount || 0))
  if (criticalCount) return 'img/dashboard-map-critical-pin.png'

  const majorCount = Math.max(...customer.devices.map((d) => d.majorAlarmCount || 0))
  if (majorCount) return 'img/dashboard-map-major-pin.png'

  const sdaMajorCount = Math.max(...customer.devices.map((d) => d.sdaMajorAlarmCount || 0))
  if (sdaMajorCount) return 'img/dashboard-map-sda-pin.png'

  return ''
}

const displayName = (customer: Customer): string => (
  `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim()
)

const PanToSelected = ({ customer }: { customer: Customer | null }) => {
  const map = useMap()
  const customerId = customer?.customerId
  const lat = customer?.latitude
  const lng = customer?.longitude

  useEffect(() => {
    if (map && customerId && lat != null && lng != null) {
      map.panTo({ lat, lng })
    }
  }, [map, customerId, lat, lng])

  return null
}

type CustomerMarkerProps = {
  customer: Customer
  isSelected: boolean
  onSelectCallback: (customerId: string | null) => void
}

const CustomerMarker = ({ customer, isSelected, onSelectCallback }: CustomerMarkerProps) => {
  const [markerRef, marker] = useAdvancedMarkerRef()
  const iconSrc = markerIcon(customer)

  return (
    <>
      <AdvancedMarker
        ref={markerRef}
        position={{ lat: customer.latitude!, lng: customer.longitude! }}
        onClick={() => onSelectCallback(customer.customerId)}
      >
        {iconSrc && <img src={iconSrc} alt="" />}
      </AdvancedMarker>

      {isSelected && (
        <InfoWindow
          anchor={marker}
          pixelOffset={[0, -4]}
          headerContent={<span style={{ fontWeight: 700, fontSize: '16px' }}>{displayName(customer)}</span>}
          onCloseClick={() => onSelectCallback(null)}
          disableAutoPan
        >
          <Marker markerInfo={customer} />
        </InfoWindow>
      )}
    </>
  )
}

const ActivityMapPanel = ({
  customersWithLocation,
  selectedCustomerId,
  onCustomerSelectCallback
}: Props) => {
  const { session: dealer } = useSessionContext()

  if (!dealer) return null

  const customersWithCoords = customersWithLocation.filter((c) => c.latitude && c.longitude)
  const selected = customersWithCoords.find((c) => c.customerId === selectedCustomerId) ?? null

  return (
    <>
      <InfoWindowStyles />
      <Map
        mapId="MAP_ID"
        defaultCenter={{ lat: dealer.latitude, lng: dealer.longitude }}
        defaultZoom={8}
        style={{ width: '100%', height: '570px', borderRadius: 12, marginBottom: -5 }}
      >
        {customersWithCoords.map((customer) => (
          <CustomerMarker
            key={customer.customerId}
            customer={customer}
            isSelected={customer.customerId === selectedCustomerId}
            onSelectCallback={onCustomerSelectCallback}
          />
        ))}

        <PanToSelected customer={selected} />
      </Map>
    </>
  )
}

export default ActivityMapPanel

const InfoWindowStyles = createGlobalStyle`
  .gm-style-iw-c {
    box-shadow: 0px 8px 16px 0px ${color('tooltipShadow')} !important;
    border: 1px solid ${color('tooltipBorder')} !important;
    outline: none !important;
    border-radius: 16px !important;
    padding-bottom: 10px !important;
    padding-right: 10px !important;
  }

  /* curved tip arrow */
  .gm-style-iw-tc::after {
    width: 25px !important;
    height: 12px !important;
    clip-path: path('M0 0 L6.5 6.24 Q12.5 12 18.5 6.24 L25 0 Z') !important;
  }

  .gm-style-iw-chr {
    padding: 0 !important;
    margin: 0 !important;
  }

  .gm-style-iw-ch {
    padding-top: 11px !important;
  }

  .gm-style-iw-chr {
    button {
      height: 40px !important;
      width: 40px !important;
      margin-right: -10px !important;

      span {
        margin: 9px !important;
      }
    }
  }

  .gm-style-iw-d {
    padding-top: 0 !important;
    margin-top: 0 !important;
    overflow: auto !important;
  }
`
